import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { loadAllRequests } from '@fc-core';
import { DvrDuration } from '../components';
import { CameraInfo } from '../models/camera/camera-info';
import { CameraState } from '../models/camera/camera-state';
import { ClipInterface } from '../models/camera/clip.interface';
import { ClipSource } from '@fc-vehicles/models/camera/clip.source';

export interface VideoInterface {
  camera: number;
  created: string;
  duration: { lower: string; upper: string; bounds: string };
  event: number;
  id: number;
  modified: string;
  playlist: string;
  source: ClipSource;
  vehicle: number;
}

export interface CameraTimeline {
  inside: DvrDuration[];
  outside: DvrDuration[];
}

@Injectable({
  providedIn: 'root',
})
export class VehicleDvrVideoService {
  constructor(private httpClient: HttpClient) {}

  getCameraInfo(cameraId: number): Observable<CameraInfo> {
    return this.httpClient.get<CameraInfo>(
      `${environment.apiUrl}api/dvr/camera/${cameraId}/`,
    );
  }

  getDvrClipByVehicleId(
    vehicleId: number,
    dateLower?: string,
    dateUpper?: string,
    type?: string,
  ): Observable<{ list: ClipInterface[]; count: number }> {
    let params: HttpParams = new HttpParams();
    if (type) params = params.set('type', type);
    params = params.set('vehicle', vehicleId);
    if (dateLower) params = params.set('duration__range_lower', dateLower);
    if (dateUpper) params = params.set('duration__range_upper', dateUpper);
    return loadAllRequests<ClipInterface>(
      `${environment.apiUrl}api/dvr/event/`,
      this.httpClient,
      params,
    );
  }

  getVideoHistory(cameraSerial: number): Observable<CameraTimeline> {
    return this.httpClient.get<CameraTimeline>(
      `${environment.apiUrl}api/dvr/timeline/${cameraSerial}/`,
    );
  }

  updateVideoClip(clip: {
    id: number;
    title: string;
    description: string;
  }): Observable<ClipInterface> {
    return this.httpClient.put<ClipInterface>(
      `${environment.apiUrl}api/dvr/event/${clip.id}/`,
      { title: clip.title, description: clip.description },
    );
  }

  deleteVideoClip(clipId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.apiUrl}api/dvr/event/${clipId}/`,
    );
  }

  postVideoRequest(
    camera: string,
    lower: string,
    upper: string,
    sources: string[],
    title: string,
    description: string,
  ): Observable<ClipInterface> {
    const payload = {
      camera,
      lower,
      upper,
      sources,
      title,
      description,
    };
    return this.httpClient.post<ClipInterface>(
      `${environment.apiUrl}api/dvr/event/`,
      payload,
    );
  }

  getCameraStatus(cameraId: number): Observable<CameraState> {
    return this.httpClient.get<CameraState>(
      `${environment.apiUrl}api/dvr/camera/${cameraId}/status/`,
    );
  }

  downloadClip(clipId: number): Observable<ArrayBuffer> {
    return this.httpClient.get(
      `${environment.apiUrl}api/dvr/video/${clipId}/download/`,
      { responseType: 'arraybuffer' },
    );
  }

  downloadVideoFile(data: ArrayBuffer, name: string): void {
    const blob = new Blob([data], { type: 'video/mp4' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  validateCamera(serial: string, vehicleId?: number): Observable<CameraInfo> {
    let params: HttpParams = new HttpParams();
    if (vehicleId) params = params.append('vehicle_id', vehicleId);
    return this.httpClient.get<CameraInfo>(
      `${environment.apiUrl}api/dvr/camera/s/${serial}/validate/`,
      { params },
    );
  }
}
